"use client";

import React from "react";
import RingSystem from "./RingSystem";

type Props = {
  children: React.ReactNode;
  className?: string;
};

export default function PremiumHeroBackground({ children, className }: Props) {
  return (
    <div className={`relative overflow-hidden ${className ?? ""}`}>
      {/* Base dark, calm gradient (deep desaturated blue → muted violet/indigo) */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,23,42,1) 0%, rgba(30,41,59,1) 50%, rgba(51,65,85,1) 100%)",
        }}
      />

      {/* Layer A: Radial Focus Glow - centered behind headline + prompt */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          background: "radial-gradient(1000px 600px at 50% 48%, rgba(99,102,241,0.05) 0%, rgba(99,102,241,0) 65%)",
        }}
      />

      {/* Very subtle radial glow behind hero headline (centered, low opacity) */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          background: "radial-gradient(800px 400px at 50% 45%, rgba(99,102,241,0.06) 0%, rgba(99,102,241,0) 60%)",
        }}
      />

      {/* Blurred blobs for depth (very subtle, muted colors) */}
      <div className="absolute -top-24 left-[-120px] h-[420px] w-[420px] rounded-full blur-3xl opacity-20 bg-indigo-500/10 animate-float" style={{ animationDuration: "20s" }} />
      <div className="absolute top-10 right-[-160px] h-[520px] w-[520px] rounded-full blur-3xl opacity-15 bg-violet-500/10 animate-float" style={{ animationDuration: "30s", animationDelay: "5s" }} />
      <div className="absolute -bottom-32 left-[15%] h-[520px] w-[520px] rounded-full blur-3xl opacity-20 bg-slate-500/10 animate-float" style={{ animationDuration: "25s", animationDelay: "10s" }} />

      {/* Subtle vignette (premium) */}
      <div className="absolute inset-0 bg-black/5 [mask-image:radial-gradient(70%_60%_at_50%_50%,black,transparent)]" />

      {/* Layer B: Fine grain/noise texture (very fine, film grain quality) */}
      <div
        className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='grain'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23grain)' opacity='0.4'/%3E%3C/svg%3E\")",
          backgroundSize: "200px 200px",
        }}
      />

      {/* Very slow gradient drift (almost imperceptible, 45s loop) */}
      <div
        className="absolute inset-0 opacity-25"
        style={{
          background: "linear-gradient(135deg, rgba(30,41,59,0.25) 0%, rgba(51,65,85,0.25) 50%, rgba(30,41,59,0.25) 100%)",
          backgroundSize: "200% 200%",
          animation: "gradientDrift 45s ease-in-out infinite",
        }}
      />

      {/* Visual anchor: Subtle depth falloff below prompt area */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[40vh]"
        style={{
          background: "linear-gradient(to top, rgba(0,0,0,0.12) 0%, transparent 100%)",
        }}
      />

      {/* Brand Ring: Full spectrum, continuous animation */}
      <div className="absolute inset-0 z-[1]">
        <RingSystem type="brand" />
      </div>

      {/* Content */}
      <div className="relative z-10">{children}</div>
    </div>
  );
}
